import React from 'react'
import LoadingButton from '@mui/lab/LoadingButton'
import { Box, Button, DialogActions, DialogContent, Typography } from '@mui/material'
import { useController, useForm } from 'react-hook-form'

import { InternalCreateCustomerInput } from '@core/api/generated'
import Dropdown from '@core/components/dropdown/dropdown'
import { TextField, TextFieldProps } from '@core/components/textfield/textfield'
import { addSnack } from '@core/snack/snack-state'

import { useCreateCustomer } from './admin-api'
import { AdminSettings } from './admin-settings-state'

export const categoryOptions = [
  { value: 'regular', name: 'Regular' },
  { value: 'demo', name: 'Demo' },
  { value: 'internal', name: 'Internal' },
]

type Values = InternalCreateCustomerInput

const defaultValues: Values = {
  name: '',
  identifier: '',
}

export const AdminAddCustomer = ({ adminSettings }: { adminSettings: AdminSettings }) => {
  const { handleClose } = adminSettings

  const createCustomerMutation = useCreateCustomer()

  const form = useForm({ defaultValues: { ...defaultValues, category: 'regular' } })
  const { register, formState, handleSubmit, control } = form
  const categoryField = useController({ name: 'category', control })

  const getProps = (field: keyof Values): TextFieldProps => {
    const error = formState.errors[field]
    let message = error?.message

    if (error?.type === 'maxLength') {
      message = field === 'identifier' ? 'Max length of 6' : 'Max length of 128'
    } else if (error?.type === 'minLength') {
      message = field === 'identifier' ? 'Min length of 2' : 'Min length of 3'
    }

    return {
      error: Boolean(error),
      helperText: message,
    }
  }

  const onSubmit = async (values: Values) => {
    const result = await createCustomerMutation.mutateAsync(values)

    if (result.error?.handled) {
      const field = result.error.field as keyof Values
      form.setError(field, { message: result.error.message })
      return
    }

    if (result.error) {
      addSnack({
        message: 'Something went wrong while creating customer',
        severity: 'error',
      })
      return
    }

    addSnack({ message: 'New customer added', severity: 'success' })

    handleClose()
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <DialogContent>
        <TextField
          autoFocus
          required
          {...register('name', {
            maxLength: 128,
            minLength: 3,
            required: 'Required',
          })}
          {...getProps('name')}
          label="Name"
          aria-label="Customer Name"
          fullWidth
        />
        <Box mt={2}>
          <TextField
            required
            {...register('identifier', {
              maxLength: 6,
              minLength: 2,
              required: 'Required',
            })}
            {...getProps('identifier')}
            label="Identifier"
            aria-label="Customer Identifier"
            fullWidth
          />
        </Box>
        <Box mt={3}>
          <Typography variant="subtitle2" gutterBottom>
            Category
          </Typography>
          <Dropdown
            value={categoryOptions.find((o) => o.value === categoryField.field.value) ?? null}
            onChange={(option) => categoryField.field.onChange(option.value)}
            options={categoryOptions}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={formState.isSubmitting}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          disabled={formState.isSubmitting || !formState.isDirty}
          loading={formState.isSubmitting}
          type="submit"
        >
          Add Customer
        </LoadingButton>
      </DialogActions>
    </Box>
  )
}
